import { AxiosResponse } from 'axios';
import HTTP from 'services/http';
import { logger } from 'winston-logger';
import { MassPrintRequest } from './mass-print-types';

/**
 * Service for mass printing FDC (Fill Delivery Confirmation) forms.
 * Handles API communication and PDF file downloads.
 */
export class MassPrintFdcService {
  private static readonly BASE_URL = '/api/tasks/fdc/mass-print';

  /**
   * Calls the mass print endpoint to generate a merged PDF of FDC forms.
   *
   * @param taskIds - Array of task IDs to print (max 10)
   * @returns Promise resolving to a PDF Blob
   * @throws Error if the API request fails
   *
   * @example
   * ```typescript
   * const blob = await MassPrintFdcService.printFdcForms([123, 456, 789]);
   * MassPrintFdcService.downloadPdf(blob);
   * ```
   */
  static async printFdcForms(taskIds: number[]): Promise<Blob> {
    try {
      const payload: MassPrintRequest = { task_ids: taskIds };

      logger.info('Requesting mass print for FDC forms', {
        taskIds,
        count: taskIds.length,
      });

      const response: AxiosResponse<Blob, any, object> = await HTTP.post(this.BASE_URL, payload, {
        responseType: 'blob',
        headers: {
          Accept: 'application/pdf',
        },
      });

      logger.info('Mass print request successful', {
        taskIds,
        blobSize: response.data.size,
      });

      return response.data;
    } catch (error) {
      logger.error('Failed to print FDC forms', {
        error,
        taskIds,
        message: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }

  /**
   * Triggers a browser download of the provided PDF blob.
   *
   * @param blob - PDF blob to download
   * @param filename - Name for the downloaded file (default: 'fdc-forms.pdf')
   *
   * @example
   * ```typescript
   * const blob = await MassPrintFdcService.printFdcForms([123, 456]);
   * MassPrintFdcService.downloadPdf(blob, 'my-forms.pdf');
   * ```
   */
  static downloadPdf(blob: Blob, filename = 'fdc-forms.pdf'): void {
    try {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      logger.info('PDF download triggered', { filename, blobSize: blob.size });
    } catch (error) {
      logger.error('Failed to download PDF', {
        error,
        filename,
        message: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }

  /**
   * Validates that the task IDs array meets requirements.
   *
   * @param taskIds - Array of task IDs to validate
   * @throws Error if validation fails
   */
  static validateTaskIds(taskIds: number[]): void {
    if (!Array.isArray(taskIds) || taskIds.length === 0) {
      throw new Error('Task IDs array cannot be empty');
    }

    if (taskIds.length > 10) {
      throw new Error('Maximum 10 tasks can be printed at once');
    }

    if (taskIds.some(id => !Number.isInteger(id) || id <= 0)) {
      throw new Error('All task IDs must be positive integers');
    }
  }
}
